/**
 * The price a customer pays for a single package once a promotional offer coupon
 * is applied — for display of a struck-through "was / now" price on the pricelist.
 *
 * PERCENTAGE ONLY, on purpose. The server's Coupon.calculate_discount PERCENTAGE
 * branch is `cart.price * (value / 100)`, which is linear, so a per-package
 * percentage shown here matches what checkout charges to the cent. Every other
 * coupon type (FLAT_RATE, FLAT_RATE_WITH_FREE_SHIPPING, PERCENTAGE_AND_FLAT_RATE,
 * VOLUME_DISCOUNT_FOR_PACKAGE, FREE_SHIPPING) is an order-level or conditional amount
 * with no honest per-card equivalent — a flat $X off the ORDER can't be attributed to
 * one card. For those this returns null and the card shows only its regular price
 * (no misleading was/now); the discount still rides through the banner and the checkout
 * Discount line. This is a deliberate display/charge distinction, not a bug: the CHARGE
 * for those types is correct via Cart#subtotal.
 *
 * Keep this the ONLY place the client computes a discounted price. If you find
 * yourself replicating a coupon calculation elsewhere, call this instead — the
 * one-implementation rule is exactly what kept the two server discount paths from
 * drifting, and it applies on the client too.
 *
 * @param {number|string} price  regular package price
 * @param {object|null} coupon   resolved offer coupon ({ couponType, value })
 * @returns {number|null} discounted price (>= 0), or null if not displayable per-card
 */
// Coupon types whose value is a straight percentage off the package price. Free
// shipping (in PERCENTAGE_WITH_FREE_SHIPPING) is an order-level extra, so it doesn't
// change the per-package price — the percentage part still shows a clean was/now.
const PERCENT_TYPES = ['PERCENTAGE', 'PERCENTAGE_WITH_FREE_SHIPPING'];

export default function discountedPrice(price, coupon) {
  if (!coupon || !PERCENT_TYPES.includes(coupon.couponType)) return null;

  const base = Number(price);
  const pct = Number(coupon.value);
  if (Number.isNaN(base) || Number.isNaN(pct) || pct <= 0) return null;

  return Math.max(0, base * (1 - pct / 100));
}

/**
 * The one-line promo description under the package name, e.g.
 * "20% off and free shipping. Use coupon FALL20". Derived from the coupon so the copy
 * always matches the actual discount + code. Returns null if there's no percentage to show.
 */
export function offerLine(coupon) {
  if (!coupon || !PERCENT_TYPES.includes(coupon.couponType)) return null;
  const pct = Number(coupon.value);
  if (Number.isNaN(pct) || pct <= 0) return null;

  const bits = [`${pct}% off`];
  if (/FREE_SHIPPING/.test(coupon.couponType)) bits.push('free shipping');
  return `${bits.join(' and ')}. Use coupon ${coupon.code}`;
}

/**
 * Format a price for a pricelist card — whole dollars read cleaner, cents only when
 * they exist. Shared so senior (PackageChooser) and elementary (PackageDisplay) format
 * the "was / now" identically. Returns null for a non-numeric input.
 */
export function money(value) {
  const n = Number(value);
  if (Number.isNaN(n)) return null;
  return n % 1 === 0 ? `$${n}` : `$${n.toFixed(2)}`;
}
